import { BASE_URL } from "@api/constant";
import {
  FB_APP_ID,
  IOS_APP_STORE_ID,
  ITUNES_APP,
  OG_IMAGE,
  OG_SITE_NAME,
  ROBOTS,
} from "@config/MetaTagsConstants";
import type { GADataProps } from "@helper/getGtmData";
import getPageDetails from "@helper/getPageDetails";
import { getArticleJSONLD } from "@helper/jsonLDHelper";
import { capitalize } from "@helper/stringTransform";
import { dateFormatWithTz, formatDate } from "@helper/useDateFormat";
import { MetaTagsProps } from "@sphtech/web2-seo-schema";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ProcessedArticleBody,
  ProcessedAuthorContent,
  ProcessedFullParagraph,
  ProcessedTagsContent,
  RawImageField,
} from "@typings/Opensearch";
import type { PageMetaDataValues } from "@typings/PageMetaData";

type ArticleMetaDataProps = {
  article: ProcessedArticleData;
  isMainArticle: boolean;
  gsChannels?: string[];
};

export default function ArticleMetaData({
  article,
  isMainArticle,
  gsChannels,
}: ArticleMetaDataProps): PageMetaDataValues {
  const keywords = getTagsName(article.tags || []);

  const { label } = getPageDetails(article.sectionName) || {
    label: article.sectionName,
  };

  const capitalizeSectionName = article.sectionName
    ? capitalize(article.sectionName)
    : article.sectionName;

  const authors = article.authors;
  const authorData = Array.isArray(authors) ? authors : [];
  // const metaAuthors =
  //   Array.isArray(authors) && authors.length > 0
  //     ? getAuthorNames(authors)
  //     : "";

  const gaData: GADataProps = {
    level2: article.sectionName || "",
    title: article.title,
    contentcat: 1,
    articleid: article.id,
    cue_articleid: article.id,
    contenttype: "1",
    pagination: 1,
    author: getAuthorsName(article.authors || []),
    infinite_scroll: isMainArticle ? "0" : "1",
    pubdate: formatDate(article.datetime, "yyyy-MM-dd HH:mm:ss"),
    content_publication_date_utc: article.datetime,
    content_update_date_utc: article.dateEdited,
    keyword: keywords,
  };
  let pageUrl = `${article.urlPath}`;
  pageUrl = pageUrl.replace(/^\/+/, "");

  const metaTitle =
    article.seoFields?.metaTitle && article.seoFields.metaTitle !== ""
      ? article.seoFields.metaTitle
      : `${article.title}, ${label} செய்திகள் - தமிழ் முரசு ${capitalizeSectionName} News in Tamil, Tamil Murasu`;
  const metaDescription =
    article.seoFields?.metaDescription &&
    article.seoFields.metaDescription.trim() !== ""
      ? article.seoFields.metaDescription
      : getFirstParagraph(article.articleBody);
  const metaRobots =
    article.seoFields?.metaRobots && article.seoFields.metaRobots === "1"
      ? "noindex, nofollow"
      : ROBOTS;

  const canonicalUrl =
    article.seoFields?.canonicalUrl ||
    new URL(`/${pageUrl}`, BASE_URL).toString();

  const ogImage = {
    url: article.ogImageUrl || article.imageField?.url,
    width: 480,
    height: 270,
  } as RawImageField;
  const defaultOgImage: string = new URL(OG_IMAGE, BASE_URL).toString();

  const additionalMetaDescriptors = [
    { tagName: "link", rel: "manifest", href: "/manifest.json" },

    ...(gsChannels?.length
      ? [
          {
            tagName: "meta",
            name: "grapeshot_tag",
            id: "grapeshot",
            "data-testid": "grapeshot",
            content: gsChannels.join(","),
          },
        ]
      : []),
  ];

  const metaTags: MetaTagsProps = {
    isArticlePage: true,
    title: metaTitle,
    description: metaDescription,
    keywords: keywords.split(","),
    ogType: "Article",
    slug: pageUrl,
    image: ogImage.url || defaultOgImage,
    publishedTime: formatDate(article.datetime, dateFormatWithTz),
    updatedTime: formatDate(article.dateEdited, dateFormatWithTz),
    jsonLd: JSON.stringify(getArticleJSONLD(article, authorData)),
    urlOrigin: BASE_URL,
    robots: metaRobots,
    fbAppId: FB_APP_ID,
    iosAppStoreId: IOS_APP_STORE_ID,
    appleItunesAppId: ITUNES_APP,
    ogTitle: article.ogFields?.ogTitle || metaTitle,
    ogDescription: article.ogFields?.ogDescription || metaDescription,
    ogSiteName: OG_SITE_NAME,
    additionalMetaDescriptors,
    canonicalUrl: canonicalUrl,
  };

  return {
    gaData,
    metaTags,
  };
}

export function getFirstParagraph(
  articleBody: ProcessedArticleBody | null,
): string {
  if (!articleBody) return "";
  const para = articleBody.find(
    (element) => element.type === "paragraph",
  ) as ProcessedFullParagraph;
  return para.value;
}

export function getTagsName(tags: ProcessedTagsContent[]): string {
  return tags.map((tag) => tag.name).join(",");
}

export function getAuthorsName(authors: ProcessedAuthorContent[]): string {
  return authors.map((author) => author.name).join("|");
}

export const getAuthorNames = (authors: ProcessedAuthorContent[]) => {
  return authors.map((a) => a.name.trim()).join(", ");
};
